/**
 * The Type / Priority pickers: a required choice from four, laid out as one row of boxes.
 *
 * A native `<button>` grid with `role="radiogroup"` rather than the design system's `Button`,
 * for the same reason `App.tsx` builds the sidebar this way — these are segmented options, not
 * actions, and wrapping `Button` here means overriding its variant styling until none of it
 * survives. The radio semantics are what screen readers need; the styling is what the box needs.
 */
import { cn } from '../../../shared';

export function SegmentedField<T extends string>({
  label,
  options,
  labels,
  hints,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: readonly T[];
  labels: Record<T, string>;
  /** Optional second line under an option's label, e.g. what choosing it commits the team to. */
  hints?: Partial<Record<T, string>>;
  value: T | null;
  onChange: (next: T) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-semibold text-zinc-800">
        {label} <span className="text-red-500">*</span>
      </div>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-4 gap-2">
        {options.map((option) => {
          const isActive = value === option;
          const hint = hints?.[option];
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              onClick={() => onChange(option)}
              className={cn(
                'rounded-lg border px-2 py-2.5 text-sm font-semibold transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isActive
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50'
              )}
            >
              {labels[option]}
              {hint && (
                <span
                  className={cn(
                    'mt-0.5 block text-[11px] font-normal leading-tight',
                    isActive ? 'text-blue-600' : 'text-zinc-500'
                  )}
                >
                  {hint}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
